import storageHelper from "@/helpers/storage";

// Create a mapping to store avatar preferences by address
const avatarCache: Record<string, string> = {};

// Add a local storage for mapping addresses to usernames
const addressToUsernameMap = new Map<string, string>();

// Function to get username for an address
const getUsernameForAddress = (address: string): string | null => {
  return addressToUsernameMap.get(address) || null;
};

// Function to set username for an address
const setUsernameForAddress = (address: string, username: string): void => {
  addressToUsernameMap.set(address, username);
  saveUsernameMappings();
};

// Save username mappings to local storage
const saveUsernameMappings = (): void => {
  localStorage.setItem('meverce.username.mappings', JSON.stringify(Array.from(addressToUsernameMap.entries())));
};

// Load username mappings from local storage
const loadUsernameMappings = (): void => {
  const mappings = localStorage.getItem('meverce.username.mappings');
  if (mappings) {
    try {
      const parsed = JSON.parse(mappings);
      addressToUsernameMap.clear();
      parsed.forEach(([address, username]) => {
        addressToUsernameMap.set(address, username);
      });
    } catch (e) {
      console.error('Failed to load username mappings', e);
    }
  }
};

// Initialize by loading saved username mappings
const initializeUsernameMappings = (): void => {
  loadUsernameMappings();
};

const avatarHelper = {
  /**
   * Gets the avatar for a specific address
   * @param address The user's address
   * @returns The path to the avatar image
   */
  getAvatarForAddress(address: string): string {
    try {
      // Check if we have the avatar in cache
      if (avatarCache[address]) {
        return require(`@/assets/images/avatar/${avatarCache[address]}`);
      }
      
      // Generate a consistent avatar for unknown addresses
      // This ensures the same address always gets the same avatar
      const avatarIndex = Math.abs(
        address.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 38
      ) + 1;
      
      const avatarFileName = `${avatarIndex}.png`;
      
      // Cache the result
      avatarCache[address] = avatarFileName;
      
      return require(`@/assets/images/avatar/${avatarFileName}`);
    } catch (e) {
      // Fallback to default avatar
      return require('@/assets/images/avatar/1.png');
    }
  },

  /**
   * Sets the avatar for the current user
   * @param address The user's address
   * @param avatarFile The avatar file name
   */
  setAvatarForAddress(address: string, avatarFile: string): void {
    avatarCache[address] = avatarFile;
    storageHelper.setLocalItem(`avatar_${address}`, avatarFile);
  },

  /**
   * Loads cached avatars from local storage
   */
  loadCachedAvatars(): void {
    // This could be called on app initialization to load all saved avatars
    const storageKeys = Object.keys(localStorage);
    storageKeys.forEach(key => {
      if (key.startsWith('avatar_')) {
        const address = key.replace('avatar_', '');
        const avatar = storageHelper.getLocalItem(key);
        if (avatar) {
          avatarCache[address] = avatar;
        }
      }
    });
  },

  getUsernameForAddress,
  setUsernameForAddress,
  initializeUsernameMappings,
};

export default avatarHelper; 